import { get } from 'lodash';
import moment from 'moment';
import { apiClient } from '../api/ApiClient';
import {
  FETCH_EMPLOYEE_SUCCESS,
  FETCH_EMPLOYEES_SUCCESS,
  FETCH_INVOICES_SUCCESS,
  FETCH_ORGANIZATION_LOCATIONS_SUCCESS,
  FETCH_ORGANIZATION_SUCCESS,
  FETCH_STATISTICS_SUCCESS,
  FETCH_TRANSACTIONS_SUCCESS,
} from './SettingsReducer';
import { prepareSearchData, SORT_ASC, SORT_DESC } from '../common/helpers/prepareSearchData';
import { handleError, showAlert } from '../common/components/alert/AlertActions';
import { hideLoader, showLoader } from '../common/components/NavbarLoader/NavbarLoaderActions';
import RouterPaths from '../constants/RouterPaths';
import AlertVariant from '../common/components/alert/AlertVariant';
import { UserRole } from '../constants/UserRole';
import { REVERSE_DASH_DATE_FORMAT } from '../common/utils/date/Date.utils';
import { customNavigate } from '../common/hooks/useCustomNavigate';
import { FAVOURITES_SEARCH_SUCCESS } from '../favourites/FavouritesReducer';

const ACCOUNT_API_ENDPOINT = '/account';
const ORGANIZATION_API_ENDPOINT = '/organization';
const PDF_TYPE = 'application/pdf';
const INVOICE = 'Invoice';

const ACCOUNT_PROFILE_API_ENDPOINT = '/account/profile';
const HTTP_STATUS_OK = 200;
const SUCCESS_MESSAGE = 'Password changed successfully';

export const searchEmployees = (values) => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .post(
      `${ACCOUNT_API_ENDPOINT}/employee/search`,
      prepareSearchData(values, { lastName: SORT_ASC, firstName: SORT_ASC }, true),
    )
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_EMPLOYEES_SUCCESS,
          payload: {
            items: get(res, 'data.data.content', []),
            totalItems: get(res, 'data.data.totalElements', 0),
          },
        });
      }
    })
    .catch((err) => {
      if (err?.response?.status === 403) {
        return dispatch(showAlert(err?.response?.data?.message, AlertVariant.DANGER));
      }
      return dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const fetchOrganizationDetails = () => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .get(`${ACCOUNT_API_ENDPOINT}/organization`)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_ORGANIZATION_SUCCESS,
          payload: {
            organizationDetails: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const fetchOrganizationStatistics =
  (organizationLocationId = null) =>
  (dispatch) => {
    let dateFrom = new Date();
    const dateTo = null;
    dateFrom = moment(dateFrom.setDate(dateFrom.getDate() - 30)).format(
      REVERSE_DASH_DATE_FORMAT,
    );

    dispatch(showLoader());
    return apiClient
      .post(`${ACCOUNT_API_ENDPOINT}/statistic`, {
        dateFrom,
        dateTo,
        organizationLocationId,
      })
      .then((res) => {
        if (res.status === 200) {
          dispatch({
            type: FETCH_STATISTICS_SUCCESS,
            payload: {
              organizationStatistics: res?.data?.data,
            },
          });
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(hideLoader()));
  };

function isLocationChanged(newLoc, oldLoc) {
  if (!oldLoc) return true;
  return (
    newLoc.location !== oldLoc.name ||
    newLoc.address !== oldLoc.address ||
    newLoc.address2 !== oldLoc.address2 ||
    newLoc.city !== oldLoc.city ||
    newLoc.county !== oldLoc.county ||
    Number(newLoc.postalCode?.value) !== Number(oldLoc.postalCode?.id)
  );
}

export const saveOrganizationDetails =
  (values, prevAdditionalLocation, locationIdsToDelete) => (dispatch) => {
    const {
      mobileNumber,
      landlinePhoneNumber,
      postalCodeId,
      location,
      address,
      address2,
      city,
      county,
      additionalLocation,
      mainLocationVersion,
      website,
    } = values;
    const locationsToSave = additionalLocation
      .filter((newLoc) => {
        const oldLoc = prevAdditionalLocation?.find((loc) => loc.id === newLoc.id);
        return isLocationChanged(newLoc, oldLoc);
      })
      .map((item) => ({
        postalCodeId: item.postalCode?.value || null,
        name: item.location?.trim() || null,
        address: item.address,
        address2: item.address2?.trim() || null,
        city: item.city,
        county: item.county,
        version: mainLocationVersion ?? null,
        id: item.id ?? null,
      }));

    const mainLocation = {
      postalCodeId: postalCodeId?.value || null,
      name: location?.trim() || null,
      address,
      address2: address2?.trim() || null,
      city,
      county,
      version: mainLocationVersion ?? null,
    };

    const filteredLocationsIdsToDelete = locationIdsToDelete?.filter((el) => el);
    const data = {
      locationIdsToDelete:
        filteredLocationsIdsToDelete.length > 0 ? filteredLocationsIdsToDelete : null,
      locationsToSave: locationsToSave && locationsToSave.length > 0 ? locationsToSave : null,
      mainLocation,
      landlinePhoneNumber: landlinePhoneNumber || null,
      website: website?.trim() || null,
      mobileNumber: mobileNumber || null,
    };

    dispatch(showLoader());
    return apiClient
      .put(`${ACCOUNT_API_ENDPOINT}/organization`, data)
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            showAlert('Organization details updated successfully', AlertVariant.SUCCESS),
          );
          dispatch(fetchOrganizationDetails());
        }
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          dispatch(showAlert('Access denied', AlertVariant.DANGER));
        } else {
          dispatch(handleError(err));
        }
      })
      .finally(() => dispatch(hideLoader()));
  };

export const fetchEmployeeDetails = (id) => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .get(`${ACCOUNT_API_ENDPOINT}/employee/${id}`)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_EMPLOYEE_SUCCESS,
          payload: {
            employeeDetails: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const removeEmployee = (id) => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .delete(`${ACCOUNT_API_ENDPOINT}/employee/${id}`)
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Employee removed successfully', AlertVariant.SUCCESS));
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const updateEmployeeDetails = (values, id, setErrors) => (dispatch) => {
  dispatch(showLoader());
  const { location, position, role, password, passwordRepeat, version } = values;
  const data = {
    password: password || null,
    passwordRepeat: passwordRepeat || null,
    locationId: location?.value,
    position: position?.value,
    isBuyer: !!role.some((item) => item === UserRole.BUYER),
    isSeller: !!role.some((item) => item === UserRole.SELLER),
    isTransactionAcceptor: !!role.some((item) => item === UserRole.TRANSACTION_MANAGEMENT),
    version,
  };
  return apiClient
    .put(`${ACCOUNT_API_ENDPOINT}/employee/${id}`, data)
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Employee updated successfully', AlertVariant.SUCCESS));
        customNavigate(RouterPaths.EMPLOYEES);
      }
    })
    .catch((err) => {
      if (err?.response?.status === 412) {
        setErrors(err?.response?.data?.data);
      } else {
        dispatch(handleError(err));
      }
    })
    .finally(() => dispatch(hideLoader()));
};

export const updateRejectedEmployeeDetails = (values, id, setErrors) => (dispatch) => {
  dispatch(showLoader());
  const { location, position, role, password, passwordRepeat, version } = values;
  const data = {
    id,
    ...values,
    password: password || null,
    passwordRepeat: passwordRepeat || null,
    locationId: location?.value,
    position: position?.value,
    isBuyer: !!role.some((item) => item === UserRole.BUYER),
    isSeller: !!role.some((item) => item === UserRole.SELLER),
    isTransactionAcceptor: !!role.some((item) => item === UserRole.TRANSACTION_MANAGEMENT),
    version,
  };
  return apiClient
    .put(`${ACCOUNT_API_ENDPOINT}/employee/rejected/${id}`, data)
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Employee updated successfully', AlertVariant.SUCCESS));
        customNavigate(RouterPaths.EMPLOYEES);
      }
    })
    .catch((err) => {
      if (err?.response?.status === 412) {
        setErrors(err?.response?.data?.data);
      } else {
        dispatch(handleError(err));
      }
    })
    .finally(() => dispatch(hideLoader()));
};

export const createEmployee = (values, setErrors) => (dispatch) => {
  dispatch(showLoader());
  const data = {
    ...values,
    locationId: values?.location?.value,
    position: values?.position?.value,
    isBuyer: values?.role?.includes(UserRole.BUYER),
    isSeller: values?.role?.includes(UserRole.SELLER),
    isTransactionAcceptor: values?.role?.includes(UserRole.TRANSACTION_MANAGEMENT),
  };
  return apiClient
    .post(`${ACCOUNT_API_ENDPOINT}/employee`, data)
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Employee created successfully', AlertVariant.SUCCESS));
        customNavigate(RouterPaths.EMPLOYEES);
      }
    })
    .catch((err) => {
      if (err?.response?.status === 412) {
        setErrors(err?.response?.data?.data);
      } else {
        dispatch(handleError(err));
      }
    })
    .finally(() => dispatch(hideLoader()));
};

export const fetchOrganizationLocations = () => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .get(`${ACCOUNT_API_ENDPOINT}/organization-locations`)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_ORGANIZATION_LOCATIONS_SUCCESS,
          payload: {
            organizationLocations: res?.data?.data,
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const searchTransactions = (values) => (dispatch) => {
  const { offset, transactionType, transactionDateFrom, transactionDateTo, search, location } =
    values;
  const sortOptions = { createdOn: SORT_DESC };

  const criteria = {
    search,
    transactionDateFrom: transactionDateFrom || null,
    transactionDateTo: transactionDateTo || null,
    transactionType: transactionType ? transactionType.value : null,
    organizationLocationId: location ? location.value : null,
  };

  const requestData = prepareSearchData({ offset }, sortOptions, false, criteria);
  dispatch(showLoader());
  return apiClient
    .post(`${ORGANIZATION_API_ENDPOINT}/transactions`, requestData)
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_TRANSACTIONS_SUCCESS,
          payload: {
            items: get(res, 'data.data.content', []),
            totalItems: get(res, 'data.data.totalElements', 0),
          },
        });
      }
    })
    .catch((err) => {
      if (err?.response?.status === 403) {
        return dispatch(showAlert(err?.response?.data?.message, AlertVariant.DANGER));
      }
      return dispatch(handleError(err));
    })
    .finally(() => dispatch(hideLoader()));
};

export const searchInvoices = (values) => (dispatch) => {
  const { location, ...rest } = values;
  const sortOptions = { createdOn: SORT_DESC };

  const preparedValues = {
    ...rest,
    organizationLocationId: location ? location.value : null,
  };

  dispatch(showLoader());
  return apiClient
    .post(
      `${ORGANIZATION_API_ENDPOINT}/invoices`,
      prepareSearchData(preparedValues, sortOptions),
    )
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_INVOICES_SUCCESS,
          payload: {
            items: get(res, 'data.data.content', []),
            totalItems: get(res, 'data.data.totalElements', 0),
          },
        });
      }
    })
    .catch((err) => dispatch(handleError(err)))
    .finally(() => dispatch(hideLoader()));
};

export const downloadInvoice =
  ({ id }) =>
  (dispatch) => {
    dispatch(showLoader());
    return apiClient
      .get(`${ORGANIZATION_API_ENDPOINT}/invoices/${id}`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      })
      .then((res) => {
        if (res.status === 200) {
          const url = URL.createObjectURL(new Blob([res.data], { type: PDF_TYPE }));
          const link = document.createElement('a');
          link.href = url;
          link.download = 'invoice.pdf';
          document.body.appendChild(link);
          link.click();
        }
      })
      .catch((err) => dispatch(handleError(err)))
      .finally(() => dispatch(hideLoader()));
  };

export const downloadInvoiceCreditNote = (id) => (dispatch) => {
  dispatch(showLoader());
  return apiClient
    .get(`${ORGANIZATION_API_ENDPOINT}/invoices/${id}/credit-note`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/pdf',
      },
    })
    .then((res) => {
      if (res.status === 200) {
        const url = URL.createObjectURL(new Blob([res.data], { type: PDF_TYPE }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'credit-note.pdf';
        document.body.appendChild(link);
        link.click();
      }
    })
    .catch((err) => dispatch(handleError(err)))
    .finally(() => dispatch(hideLoader()));
};

export const changeAccountPassword = (values) => async (dispatch) => {
  try {
    const response = await apiClient.put(ACCOUNT_PROFILE_API_ENDPOINT, values);

    if (response.status === HTTP_STATUS_OK) {
      dispatch(
        showAlert(SUCCESS_MESSAGE, AlertVariant.SUCCESS, 4000, false, null, 'custom', true),
      );
      return;
    }

    throw new Error(`Unexpected response status: ${response.status}`);
  } catch (error) {
    dispatch(handleError(error));
    throw error;
  }
};

export const getFavouriteAuctions = (body) => async (dispatch) => {
  try {
    const response = await apiClient.post(`/account/favourite/search`, body);
    if (response?.status === 200) {
      dispatch({
        type: FAVOURITES_SEARCH_SUCCESS,
        payload: { totalItems: get(response, 'data.data.totalElements', 0) },
      });
    }
    return response;
  } catch (error) {
    return error.response;
  }
};

export const addFavouriteAuction =
  ({ memberId, auctionId, organizationLocationId }) =>
  async (dispatch) => {
    dispatch(showLoader());
    try {
      const response = await apiClient.put(
        organizationLocationId
          ? `/account/favourite/${memberId}/${organizationLocationId}/${auctionId}`
          : `/account/favourite/${memberId}/${auctionId}/all-locations`,
      );
      if (response.status === 200) {
        dispatch(
          showAlert(
            'The offer has been successfully added to your favourites list.',
            AlertVariant.SUCCESS,
            5000,
            false,
            null,
            'bottom-left',
            false,
          ),
        );
      }
      return response;
    } catch (error) {
      dispatch(handleError(error));
      return error.response;
    } finally {
      dispatch(hideLoader());
    }
  };

export const deleteFavouriteAuction =
  ({ memberId, auctionId, organizationLocationId }) =>
  async (dispatch) => {
    dispatch(showLoader());
    try {
      const response = await apiClient.delete(
        organizationLocationId
          ? `/account/favourite/${memberId}/${organizationLocationId}/${auctionId}`
          : `/account/favourite/${memberId}/${auctionId}`,
      );
      if (response.status === 200) {
        dispatch(
          showAlert(
            'The offer has been successfully removed from your favourites list.',
            AlertVariant.SUCCESS,
            5000,
            false,
            null,
            'bottom-left',
            false,
          ),
        );
      }
      return response;
    } catch (error) {
      dispatch(handleError(error));
      return error.response;
    } finally {
      dispatch(hideLoader());
    }
  };
